import { reactive } from 'vue';

import { cloneDeep } from 'lodash-es';

import { SlateAuth } from '@/api/auth/SlateAuth';

let loading = false;

interface AccountPeriodDetails {
  loaded: boolean;
  list: Array<any>;
  earliest: Date;
  isClosed: (passed_date: Date | string) => boolean;
}
export const accountingPeriods: AccountPeriodDetails = reactive({
  'loaded': false,
  'list': [],
  'earliest': getFirstOfThisMonth(),
  'isClosed': isClosed,
});
export function useAccountingPeriods() {
  getList();
  return accountingPeriods;
}

setInterval(() => {
  getList();
}, 3600000);

function isClosed(passed_date: Date | string): boolean {
  let check_date: string;
  if (passed_date instanceof Date) {
    const year = passed_date.getUTCFullYear();
    const month = (passed_date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = passed_date.getUTCDate().toString().padStart(2, '0');
    check_date = `${year}-${month}-${day}`;
  } else if (typeof passed_date === 'string') {
    check_date = cloneDeep(passed_date);
  } else {
    return true;
  }

  if (
    accountingPeriods.list.some((item) => {
      if (item.AcctSeed__Status__c != 'Open') return false;
      return check_date >= item.AcctSeed__Start_Date__c && check_date <= item.AcctSeed__End_Date__c;
    })
  ) {
    return false;
  }

  return true;
}

function getList() {
  if (!SlateAuth.token) return;
  if (loading) return;

  loading = true;

  const headers = Object.assign({}, getBaseHeaders());
  const url = new URL(getAPIUrl());

  return fetch(url, {
    'method': 'GET',
    'credentials': 'same-origin',
    'cache': 'no-cache',
    'headers': headers,
  })
    .then(
      (response) => {
        if (response.status === 401) {
          SlateAuth.logout();
          throw new Error('401: Unauthorized / User not logged in');
        }
        if (response.status == 404) {
          throw new Error('404: Object Not Found');
        }
        if (response.status >= 500) {
          throw new Error(`${response.status}: Server Error\n\n${response.statusText}`);
        }

        return response.json();
      },
      () => {
        accountingPeriods.loaded = false;
        accountingPeriods.list = [];
        accountingPeriods.earliest = getFirstOfThisMonth();
      },
    )
    .then((response_data) => {
      accountingPeriods.list = JSON.parse(response_data.payload);
      if (response_data.earliest) {
        const parts = response_data.earliest.split('-');
        accountingPeriods.earliest = new Date(
          parseInt(parts[0], 10),
          parseInt(parts[1], 10) - 1,
          parseInt(parts[2], 10),
        );
      }
      loading = false;
      accountingPeriods.loaded = true;
    });
}

function getAPIUrl(override_url?: string) {
  const apiBaseUrl: string = import.meta.env.VUE_API_URL ? import.meta.env.VUE_API_URL : '/api/v1/';
  const endpointUrl: string = 'logistics/accounting_periods/';
  const url = `${window.location.origin}${apiBaseUrl}${endpointUrl}`;
  return url;
}

function getCookie(name: string): string | null {
  let cookieValue = null;

  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      if (!Array.isArray(cookies)) continue;
      if (cookies[i] === undefined) continue;

      const cookie = cookies[i]?.trim() ?? '';

      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }

  return cookieValue;
}

function getBaseHeaders(): Record<string, any> {
  const headers: Record<string, any> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const csrftoken = getCookie('csrftoken');

  if (csrftoken) {
    headers['X-CSRFToken'] = csrftoken;
  }

  if (SlateAuth.token) {
    headers['Authorization'] = SlateAuth.token;
  }

  return headers;
}

function getFirstOfThisMonth() {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
}
